import { ListQueryParams } from "@/models/table";
import axios from "axios";

/**
 * Controllers for executed share buybacks (epic &118).
 *
 * These read *filed execution* — what a company actually spent buying its own stock — and are
 * deliberately separate from the buyback **events** under `controllers/events.ts`, which serve
 * announced programmes. The two routinely disagree, because an authorization is not a purchase
 * and a large share of announced programmes are never fully executed.
 */

const BASE = "/interface/api/buybacks";

export interface BuybackPeriod {
    periodDate: string;
    publishedAt: string | null;
    /** The accession the figure came from, and whether it was differenced out of a YTD total. */
    sourceRef: string;
    value: number | null;
    shares: number | null;
    remainingAuthorization: number | null;
}

export interface StockBuybacks {
    hasData: boolean;
    latestPeriod: string | null;
    latestValue: number | null;
    latestShares: number | null;
    trailingValue: number | null;
    trailingShares: number | null;
    remainingAuthorization: number | null;
    authorizationAsOf: string | null;
    periods: BuybackPeriod[];
}

/** One company's quarterly repurchase history and headline figures. */
export const getStockBuybacks = async (tradingitemId: number, signal?: AbortSignal): Promise<StockBuybacks> => {
    const res = await axios.get(`${BASE}/stock/${tradingitemId}`, { signal });
    return res.data;
};

/** Every company with a filed repurchase in the recent window — the universe grid. */
export const getBuybackUniverse = async (params: ListQueryParams) => {
    const { page_size, page, filter_options, signal, next_page_url } = params;
    const url = next_page_url || `${BASE}/universe/list`;
    const defaultParams = next_page_url ? {} : { page_size, page, ...filter_options };

    const res = await axios.get(url, { params: { ...defaultParams }, signal });
    return res.data;
};

export interface BuybackSummary {
    companies: number;
    totalValue: number | null;
    totalAuthorization: number | null;
    largestName: string;
    largestValue: number | null;
    /** Share of all trailing spend from the ten biggest buyers. */
    topTenSharePct: number | null;
    latestPeriod: string | null;
}

/** Market-wide totals for the stat cards above the grid. */
export const getBuybackSummary = async (signal?: AbortSignal): Promise<BuybackSummary> => {
    const res = await axios.get(`${BASE}/summary`, { signal });
    return res.data;
};
